import { useState, useMemo } from 'react';
import {
  streakLevels,
  streakUsers,
  streakHistory,
  streakStats,
  streakRules,
  activityConfigs,
  rewardConfigs,
  getTopStreaks,
  getUsersAtRisk,
  getRecentHistory,
  getLevelDistribution,
  getActivityDistribution,
  getStreakLevelConfig,
  getCurrentLevelConfig,
  getNextLevel,
  type StreakLevel,
  type ActivityType,
  type StreakUser,
} from '@/mocks/rfwStreaks';

type SidebarView = 'rachas' | 'niveles' | 'actividades' | 'recompensas' | 'reglas';
type SortMode = 'racha' | 'puntos' | 'nivel';

export default function RachasPage() {
  const [view, setView] = useState<SidebarView>('rachas');
  const [selectedLevel, setSelectedLevel] = useState<StreakLevel | 'todas'>('todas');
  const [selectedActivity, setSelectedActivity] = useState<ActivityType | 'todas'>('todas');
  const [sortMode, setSortMode] = useState<SortMode>('racha');
  const [expandedUser, setExpandedUser] = useState<string | null>(null);

  const topStreaks = useMemo(() => getTopStreaks(10), []);
  const atRiskUsers = useMemo(() => getUsersAtRisk(), []);
  const recentHistory = useMemo(() => getRecentHistory(10), []);
  const levelDist = useMemo(() => getLevelDistribution(), []);
  const activityDist = useMemo(() => getActivityDistribution(), []);

  const filteredUsers = useMemo(() => {
    let users = [...streakUsers];
    if (selectedLevel !== 'todas') {
      users = users.filter((u) => u.currentLevel === selectedLevel);
    }
    if (selectedActivity !== 'todas') {
      users = users.filter((u) => u.activeActivities.includes(selectedActivity));
    }
    if (sortMode === 'racha') {
      users.sort((a, b) => b.currentStreak - a.currentStreak);
    } else if (sortMode === 'puntos') {
      users.sort((a, b) => b.pointsEarned - a.pointsEarned);
    } else {
      users.sort((a, b) => b.level - a.level);
    }
    return users;
  }, [selectedLevel, selectedActivity, sortMode]);

  const getHistoryForUser = (userId: string) => {
    return streakHistory.filter((h) => h.userId === userId).slice(0, 5);
  };

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-amber-500 via-red-500 to-fuchsia-600 overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-white/5 rounded-full translate-y-1/3 -translate-x-1/4 blur-3xl"></div>
        <div className="relative max-w-6xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="flex items-center gap-2 text-white/70 text-xs mb-4">
            <a href="/" className="hover:text-white transition-colors">RFW</a>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">Sistema de Rachas</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center">
                  <i className="ri-fire-fill text-2xl text-white"></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-4xl font-bold text-white">Sistema de Rachas</h1>
                  <p className="text-white/70 text-sm">Crear hábito diario</p>
                </div>
              </div>
              <p className="text-white/80 text-sm md:text-base max-w-lg">
                Cada día que interactuás con RFW suma a tu racha. Escuchá, comentá, compartí, participá o entrá a una cabina. Mientras más días seguidos, más recompensas.
              </p>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-white">{streakStats.totalActiveStreaks.toLocaleString()}</div>
                <div className="text-xs text-white/70">Rachas Activas</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white">{streakStats.longestActiveStreak}</div>
                <div className="text-xs text-white/70">Racha Más Larga</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white">{streakStats.todayCompleted.toLocaleString()}</div>
                <div className="text-xs text-white/70">Hoy</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Live Pulse Bar */}
      <section className="bg-background-100 border-b border-background-200/70">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 flex flex-wrap items-center gap-4 md:gap-6 overflow-x-auto">
          <div className="flex items-center gap-2 whitespace-nowrap">
            <span className="w-2 h-2 rounded-full bg-fuchsia-500 animate-pulse"></span>
            <span className="text-xs font-semibold text-foreground-700">EN VIVO</span>
          </div>
          {[
            { icon: 'ri-fire-line', color: 'text-amber-600', value: streakStats.totalActiveStreaks.toLocaleString(), label: 'Rachas' },
            { icon: 'ri-trophy-line', color: 'text-fuchsia-600', value: streakStats.longestActiveStreak.toString(), label: 'Más Larga' },
            { icon: 'ri-check-double-line', color: 'text-emerald-600', value: streakStats.todayCompleted.toLocaleString(), label: 'Hoy' },
            { icon: 'ri-arrow-up-line', color: 'text-sky-600', value: streakStats.levelUpsToday.toLocaleString(), label: 'Subieron' },
            { icon: 'ri-alert-line', color: 'text-rose-600', value: streakStats.streaksAtRisk.toLocaleString(), label: 'En Riesgo' },
            { icon: 'ri-coin-line', color: 'text-amber-600', value: `${(streakStats.totalPointsAwarded / 1000).toFixed(0)}K`, label: 'Puntos' },
          ].map((m, i) => (
            <div key={i} className="flex items-center gap-1.5 whitespace-nowrap text-xs">
              <i className={`${m.icon} ${m.color}`}></i>
              <span className="font-semibold text-foreground-950">{m.value}</span>
              <span className="text-foreground-500">{m.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar */}
          <aside className="w-full lg:w-56 flex-shrink-0">
            <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
              {([
                { key: 'rachas', label: 'Rachas Activas', icon: 'ri-fire-line', color: 'text-amber-600' },
                { key: 'niveles', label: 'Niveles', icon: 'ri-stack-line', color: 'text-fuchsia-600' },
                { key: 'actividades', label: 'Actividades', icon: 'ri-task-line', color: 'text-sky-600' },
                { key: 'recompensas', label: 'Recompensas', icon: 'ri-gift-line', color: 'text-rose-600' },
                { key: 'reglas', label: 'Reglas', icon: 'ri-scales-line', color: 'text-emerald-600' },
              ] as { key: SidebarView; label: string; icon: string; color: string }[]).map((item) => (
                <button
                  key={item.key}
                  onClick={() => setView(item.key)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                    view === item.key
                      ? 'bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200'
                      : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                  }`}
                >
                  <i className={`${item.icon} ${view === item.key ? 'text-fuchsia-600' : ''}`}></i>
                  {item.label}
                </button>
              ))}
            </nav>

            {/* Level quick nav */}
            <div className="hidden lg:block mt-5 pt-5 border-t border-background-200/70">
              <div className="text-xs font-semibold text-foreground-500 mb-2 uppercase tracking-wide">Niveles de Racha</div>
              <div className="space-y-1">
                {streakLevels.map((lvl) => {
                  const count = levelDist.find((d) => d.level === lvl.days)?.count ?? 0;
                  return (
                    <button
                      key={lvl.days}
                      onClick={() => { setView('rachas'); setSelectedLevel(lvl.days); }}
                      className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs transition-colors cursor-pointer ${
                        selectedLevel === lvl.days
                          ? `${lvl.bgColor} ${lvl.color}`
                          : 'text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      <i className={`${lvl.icon}`}></i>
                      <span className="flex-1 text-left">{lvl.label}</span>
                      <span className="text-xs opacity-70">{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>

          {/* Main Area */}
          <div className="flex-1 min-w-0">
            {/* View: Rachas Activas */}
            {view === 'rachas' && (
              <div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
                  <h2 className="text-lg font-bold text-foreground-950 flex items-center gap-2">
                    <i className="ri-fire-line text-amber-600"></i>
                    Rachas Activas
                    <span className="text-sm font-normal text-foreground-500">({filteredUsers.length} oyentes)</span>
                  </h2>
                  <div className="flex items-center gap-3">
                    {/* Filters */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <select
                        value={selectedLevel}
                        onChange={(e) => setSelectedLevel(e.target.value === 'todas' ? 'todas' : Number(e.target.value) as StreakLevel)}
                        className="text-xs bg-background-100 border border-background-200/70 rounded-md px-2 py-1.5 text-foreground-700 cursor-pointer"
                      >
                        <option value="todas">Todos los niveles</option>
                        {streakLevels.map((l) => (
                          <option key={l.days} value={l.days}>{l.label}</option>
                        ))}
                      </select>
                      <select
                        value={selectedActivity}
                        onChange={(e) => setSelectedActivity(e.target.value === 'todas' ? 'todas' : e.target.value as ActivityType)}
                        className="text-xs bg-background-100 border border-background-200/70 rounded-md px-2 py-1.5 text-foreground-700 cursor-pointer"
                      >
                        <option value="todas">Todas las actividades</option>
                        {activityConfigs.map((a) => (
                          <option key={a.key} value={a.key}>{a.label}</option>
                        ))}
                      </select>
                      <select
                        value={sortMode}
                        onChange={(e) => setSortMode(e.target.value as SortMode)}
                        className="text-xs bg-background-100 border border-background-200/70 rounded-md px-2 py-1.5 text-foreground-700 cursor-pointer"
                      >
                        <option value="racha">Por Racha</option>
                        <option value="puntos">Por Puntos</option>
                        <option value="nivel">Por Nivel</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* At Risk Alert */}
                {atRiskUsers.length > 0 && (
                  <div className="mb-5 p-3 bg-rose-50 border border-rose-200 rounded-lg flex flex-wrap items-center gap-2">
                    <i className="ri-alert-line text-rose-600"></i>
                    <span className="text-sm text-rose-700 font-medium">¡{atRiskUsers.length} rachas en riesgo hoy!</span>
                    <div className="flex flex-wrap gap-1.5">
                      {atRiskUsers.map((u) => (
                        <span key={u.id} className="px-2 py-0.5 bg-rose-100 text-rose-700 rounded-full text-xs font-medium">
                          {u.name.split(' ')[0]} ({u.currentStreak}d)
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* User Cards */}
                <div className="space-y-4">
                  {filteredUsers.map((user) => {
                    const levelCfg = getCurrentLevelConfig(user.currentStreak);
                    const nextLvl = getNextLevel(user.currentStreak);
                    const isExpanded = expandedUser === user.id;
                    const userHistory = getHistoryForUser(user.id);

                    return (
                      <div
                        key={user.id}
                        className={`bg-background-50 rounded-lg border transition-all ${
                          user.riskOfLosing ? 'border-rose-200' : 'border-background-200/70'
                        } ${isExpanded ? 'shadow-sm' : ''}`}
                      >
                        {/* Card Header */}
                        <div
                          onClick={() => setExpandedUser(isExpanded ? null : user.id)}
                          className="p-4 flex items-center gap-4 cursor-pointer"
                        >
                          {/* Rank */}
                          <div className="w-8 text-center flex-shrink-0">
                            <span className="text-sm font-bold text-foreground-400">#{user.rankingPosition}</span>
                          </div>

                          {/* Avatar */}
                          <div className="relative w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                            <img src={user.photo} alt={user.name} className="w-full h-full object-cover" />
                            {user.todayCompleted && (
                              <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 bg-emerald-500 rounded-full border-2 border-background-50 flex items-center justify-center">
                                <i className="ri-check-line text-white text-xs"></i>
                              </div>
                            )}
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="font-semibold text-sm text-foreground-950 truncate">{user.name}</span>
                              {user.isVip && (
                                <span className="px-1.5 py-0.5 bg-fuchsia-100 text-fuchsia-700 rounded-full text-xs font-medium whitespace-nowrap">VIP</span>
                              )}
                              <span className="text-xs text-foreground-400">Nv.{user.level}</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-foreground-500">
                              <span>Desde {user.streakStartedRelative}</span>
                              <span>·</span>
                              <span>Mejor: {user.bestStreak}d</span>
                              <span>·</span>
                              <span>{user.totalDays} días totales</span>
                            </div>
                          </div>

                          {/* Streak Badge */}
                          <div className="flex-shrink-0 text-center">
                            <div className={`flex items-center gap-1 ${levelCfg.color}`}>
                              <i className={`${levelCfg.icon} text-xl`}></i>
                              <span className="text-2xl font-bold">{user.currentStreak}</span>
                            </div>
                            <div className="text-xs text-foreground-400">días</div>
                          </div>

                          {/* Progress mini */}
                          {nextLvl && (
                            <div className="hidden md:block flex-shrink-0 w-24">
                              <div className="flex items-center justify-between text-xs text-foreground-500 mb-0.5">
                                <span>{levelCfg.label}</span>
                                <span>{nextLvl.days}d</span>
                              </div>
                              <div className="w-full h-1.5 bg-background-100 rounded-full overflow-hidden">
                                <div
                                  className={`h-full bg-gradient-to-r ${levelCfg.gradient} rounded-full`}
                                  style={{ width: `${Math.min(100, (user.currentStreak / nextLvl.days) * 100)}%` }}
                                ></div>
                              </div>
                            </div>
                          )}

                          {isExpanded ? (
                        <i className="ri-arrow-up-s-line text-foreground-400"></i>
                      ) : (
                        <i className="ri-arrow-down-s-line text-foreground-400"></i>
                      )}
                        </div>

                        {/* Expanded */}
                        {isExpanded && (
                          <div className="px-4 pb-4 border-t border-background-200/70 pt-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {/* Left: Stats & Activities */}
                              <div>
                                <div className="text-xs font-semibold text-foreground-500 uppercase tracking-wide mb-2">Actividades</div>
                                <div className="flex flex-wrap gap-1.5 mb-3">
                                  {user.activeActivities.map((act) => {
                                    const cfg = activityConfigs.find((a) => a.key === act);
                                    return (
                                      <span key={act} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${cfg?.bgColor ?? 'bg-background-100'} ${cfg?.color ?? 'text-foreground-600'}`}>
                                        <i className={`${cfg?.icon ?? 'ri-check-line'} text-xs`}></i>
                                        {cfg?.label ?? act}
                                      </span>
                                    );
                                  })}
                                </div>

                                <div className="text-xs font-semibold text-foreground-500 uppercase tracking-wide mb-2">Insignias</div>
                                <div className="flex flex-wrap gap-1.5 mb-3">
                                  {user.badgesUnlocked.length > 0 ? user.badgesUnlocked.map((b, i) => (
                                    <span key={i} className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs font-medium border border-amber-200">
                                      {b}
                                    </span>
                                  )) : (
                                    <span className="text-xs text-foreground-400 italic">Sin insignias aún</span>
                                  )}
                                </div>

                                <div className="text-xs font-semibold text-foreground-500 uppercase tracking-wide mb-2">Datos</div>
                                <div className="grid grid-cols-3 gap-2 text-center">
                                  <div className="bg-background-100 rounded-md p-2">
                                    <div className="text-lg font-bold text-foreground-950">{user.pointsEarned.toLocaleString()}</div>
                                    <div className="text-xs text-foreground-500">Puntos</div>
                                  </div>
                                  <div className="bg-background-100 rounded-md p-2">
                                    <div className="text-lg font-bold text-foreground-950">{user.totalDays}</div>
                                    <div className="text-xs text-foreground-500">Total días</div>
                                  </div>
                                  <div className="bg-background-100 rounded-md p-2">
                                    <div className="text-lg font-bold text-foreground-950">{user.bestStreak}</div>
                                    <div className="text-xs text-foreground-500">Mejor racha</div>
                                  </div>
                                </div>

                                {user.riskOfLosing && (
                                  <div className="mt-3 flex items-center gap-2 text-sm text-rose-600 bg-rose-50 rounded-md px-3 py-2">
                                    <i className="ri-alert-fill"></i>
                                    <span>¡Completá una actividad hoy o tu racha de {user.currentStreak} días se reinicia!</span>
                                  </div>
                                )}
                              </div>

                              {/* Right: History */}
                              <div>
                                <div className="text-xs font-semibold text-foreground-500 uppercase tracking-wide mb-2">Historial Reciente</div>
                                {userHistory.length > 0 ? (
                                  <div className="space-y-2">
                                    {userHistory.map((entry) => (
                                      <div key={entry.id} className="flex items-start gap-2 text-xs">
                                        <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${entry.levelUp ? 'bg-fuchsia-500' : 'bg-background-300'}`}></div>
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-1.5 flex-wrap">
                                            <span className="text-foreground-500">{entry.dateRelative}</span>
                                            {entry.levelUp && (
                                              <span className="px-1 py-0.5 bg-fuchsia-100 text-fuchsia-700 rounded text-xs font-medium">¡Subió a {entry.newLevel}d!</span>
                                            )}
                                            {entry.badgeUnlock && (
                                              <span className="px-1 py-0.5 bg-amber-50 text-amber-700 rounded text-xs font-medium">{entry.badgeUnlock}</span>
                                            )}
                                          </div>
                                          <div className="text-foreground-600 mt-0.5">{entry.description}</div>
                                          <div className="text-foreground-400 mt-0.5">+{entry.pointsEarned} pts</div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-xs text-foreground-400 italic">Sin historial disponible</div>
                                )}
                              </div>
                            </div>

                            {/* Full progress bar */}
                            {nextLvl && (
                              <div className="mt-4 pt-4 border-t border-background-200/70">
                                <div className="flex items-center justify-between text-xs text-foreground-500 mb-1.5">
                                  <span>Progreso hacia {nextLvl.days} días</span>
                                  <span>{user.currentStreak} / {nextLvl.days} días</span>
                                </div>
                                <div className="w-full h-2 bg-background-100 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full bg-gradient-to-r ${levelCfg.gradient} rounded-full transition-all`}
                                    style={{ width: `${Math.min(100, (user.currentStreak / nextLvl.days) * 100)}%` }}
                                  ></div>
                                </div>
                                {user.daysUntilNextLevel > 0 && (
                                  <div className="text-xs text-foreground-400 mt-1">
                                    {user.daysUntilNextLevel === 1 ? '¡Mañana subís de nivel!' : `Faltan ${user.daysUntilNextLevel} días para subir a ${nextLvl.days} días`}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* View: Niveles */}
            {view === 'niveles' && (
              <div>
                <h2 className="text-lg font-bold text-foreground-950 flex items-center gap-2 mb-5">
                  <i className="ri-stack-line text-fuchsia-600"></i>
                  Niveles de Racha
                </h2>
                <div className="space-y-4">
                  {streakLevels.map((lvl) => {
                    const usersAtLevel = streakUsers.filter((u) => u.currentLevel === lvl.days);
                    const count = levelDist.find((d) => d.level === lvl.days)?.count ?? 0;
                    return (
                      <div key={lvl.days} className={`bg-background-50 rounded-lg border border-background-200/70 overflow-hidden`}>
                        <div className={`p-4 bg-gradient-to-r ${lvl.gradient} text-white`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                                <i className={`${lvl.icon} text-2xl`}></i>
                              </div>
                              <div>
                                <div className="font-bold text-lg">{lvl.label}</div>
                                <div className="text-sm text-white/80">{lvl.description}</div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold">{count}</div>
                              <div className="text-sm text-white/70">oyentes</div>
                            </div>
                          </div>
                          <div className="mt-2 flex items-center gap-2 text-sm text-white/80">
                            <i className="ri-coin-line"></i>
                            <span>Multiplicador x{lvl.rewardMultiplier} en puntos</span>
                          </div>
                        </div>
                        {usersAtLevel.length > 0 && (
                          <div className="p-4">
                            <div className="text-xs font-semibold text-foreground-500 uppercase tracking-wide mb-3">Oyentes en este nivel</div>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                              {usersAtLevel.map((u) => (
                                <div key={u.id} className="flex items-center gap-2.5 p-2 bg-background-100 rounded-lg">
                                  <img src={u.photo} alt={u.name} className="w-9 h-9 rounded-full object-cover" />
                                  <div className="min-w-0 flex-1">
                                    <div className="text-xs font-medium text-foreground-950 truncate">{u.name}</div>
                                    <div className="text-xs text-foreground-500">{u.currentStreak}d · Nv.{u.level}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* View: Actividades */}
            {view === 'actividades' && (
              <div>
                <h2 className="text-lg font-bold text-foreground-950 flex items-center gap-2 mb-5">
                  <i className="ri-task-line text-sky-600"></i>
                  Actividades Diarias
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {activityConfigs.map((act) => {
                    const count = activityDist.find((d) => d.activity === act.key)?.count ?? 0;
                    return (
                      <div key={act.key} className="bg-background-50 rounded-lg p-5 border border-background-200/70 hover:border-fuchsia-200 transition-colors">
                        <div className="flex items-start gap-4">
                          <div className={`w-14 h-14 rounded-xl ${act.bgColor} flex items-center justify-center flex-shrink-0`}>
                            <i className={`${act.icon} text-2xl ${act.color}`}></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-base text-foreground-950 mb-1">{act.label}</div>
                            <div className="text-sm text-foreground-600 mb-3">{act.description}</div>
                            <div className="flex items-center gap-3 text-xs">
                              <span className="bg-amber-50 text-amber-700 px-2.5 py-1 rounded-full font-medium">+{act.basePoints} pts base</span>
                              <span className="bg-fuchsia-50 text-fuchsia-700 px-2.5 py-1 rounded-full font-medium">+{act.streakBonus} bonus/día</span>
                            </div>
                            {act.minMinutes && (
                              <div className="mt-2 text-xs text-foreground-400 flex items-center gap-1">
                                <i className="ri-time-line"></i>
                                <span>Mínimo {act.minMinutes} minutos</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* View: Recompensas */}
            {view === 'recompensas' && (
              <div>
                <h2 className="text-lg font-bold text-foreground-950 flex items-center gap-2 mb-5">
                  <i className="ri-gift-line text-rose-600"></i>
                  Recompensas
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {rewardConfigs.map((reward) => (
                    <div key={reward.key} className="bg-background-50 rounded-lg p-5 border border-background-200/70 hover:border-fuchsia-200 transition-colors text-center">
                      <div className={`w-16 h-16 mx-auto rounded-2xl ${reward.bgColor} flex items-center justify-center mb-4`}>
                        <i className={`${reward.icon} text-3xl ${reward.color}`}></i>
                      </div>
                      <div className={`font-bold text-base ${reward.color} mb-2`}>{reward.label}</div>
                      <div className="text-sm text-foreground-600 mb-3">{reward.description}</div>
                      <div className="inline-flex items-center gap-1.5 text-xs text-foreground-400 bg-background-100 px-3 py-1.5 rounded-full">
                        <i className="ri-lock-unlock-line"></i>
                        <span>{reward.unlockAt}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Multiplier Table */}
                <div className="bg-background-50 rounded-lg p-5 border border-background-200/70">
                  <div className="text-sm font-semibold text-foreground-950 mb-4">Tabla de Multiplicadores</div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-background-200/70">
                          <th className="text-left py-2 px-3 text-xs font-semibold text-foreground-500 uppercase">Nivel</th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-foreground-500 uppercase">Días</th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-foreground-500 uppercase">Multiplicador</th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-foreground-500 uppercase">Ejemplo (Escuchar 15min)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {streakLevels.map((lvl) => (
                          <tr key={lvl.days} className="border-b border-background-50">
                            <td className="py-2.5 px-3">
                              <span className={`inline-flex items-center gap-1.5 ${lvl.color}`}>
                                <i className={lvl.icon}></i>
                                <span className="font-medium">{lvl.label}</span>
                              </span>
                            </td>
                            <td className="py-2.5 px-3 text-foreground-600">{lvl.days} días</td>
                            <td className="py-2.5 px-3">
                              <span className={`font-bold ${lvl.color}`}>x{lvl.rewardMultiplier}</span>
                            </td>
                            <td className="py-2.5 px-3 text-foreground-600">{Math.round(10 * lvl.rewardMultiplier)} puntos</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* View: Reglas */}
            {view === 'reglas' && (
              <div>
                <h2 className="text-lg font-bold text-foreground-950 flex items-center gap-2 mb-5">
                  <i className="ri-scales-line text-emerald-600"></i>
                  Reglas del Sistema
                </h2>
                <div className="space-y-4 mb-6">
                  {streakRules.map((rule) => (
                    <div key={rule.id} className="bg-background-50 rounded-lg p-5 border border-background-200/70">
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-xl ${rule.bgColor} flex items-center justify-center flex-shrink-0`}>
                          <i className={`${rule.icon} text-lg ${rule.color}`}></i>
                        </div>
                        <div>
                          <div className="font-semibold text-base text-foreground-950 mb-2">{rule.title}</div>
                          <div className="text-sm text-foreground-600 leading-relaxed">{rule.description}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Protection Example */}
                <div className="bg-emerald-50 rounded-lg p-5 border border-emerald-200">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-shield-check-line text-lg text-emerald-600"></i>
                    </div>
                    <div>
                      <div className="font-semibold text-sm text-emerald-800 mb-2">Ejemplo: Protección Técnica en Acción</div>
                      <div className="text-sm text-emerald-700 leading-relaxed">
                        El 15 de Junio, RFW experimentó una caída de 45 minutos en los servidores de streaming. Automáticamente, todos los oyentes con rachas activas recibieron un "día de gracia". Las 12,480 rachas activas no se vieron afectadas. El sistema registró el incidente y notificó a cada oyente: "Tu racha está a salvo — hubo un fallo técnico y no cuenta como día perdido."
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Cross Links */}
        <div className="mt-10 pt-8 border-t border-background-200/70">
          <div className="text-xs font-semibold text-foreground-500 uppercase tracking-wide mb-3">También te puede interesar</div>
          <div className="flex flex-wrap gap-2">
            {[
              { href: '/gamificacion', label: 'Gamificación', icon: 'ri-gamepad-line' },
              { href: '/niveles', label: 'Niveles', icon: 'ri-bar-chart-line' },
              { href: '/insignias', label: 'Insignias', icon: 'ri-shield-star-line' },
              { href: '/logros', label: 'Logros', icon: 'ri-medal-line' },
              { href: '/ranking', label: 'Rankings', icon: 'ri-trophy-line' },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-background-100 rounded-full text-xs text-foreground-600 hover:bg-fuchsia-50 hover:text-fuchsia-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className={link.icon}></i>
                {link.label}
              </a>
            ))}
          </div>
        </div>

        {/* Final Rule */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-fuchsia-50 border border-fuchsia-200 rounded-full">
            <i className="ri-shield-check-line text-fuchsia-600"></i>
            <span className="text-sm font-semibold text-fuchsia-700">No perder racha por fallos técnicos.</span>
          </div>
        </div>
      </div>
    </div>
  );
}